import type { PairingStateReply, StoredRide } from '../shared/trip-api.js';
import type { BrowserStorage } from './browser-storage.js';
import { observedRequest } from './observed-request.js';

export const PLANNER_KEY_STORAGE_KEY = 'ridge-and-road:planner-key';
const KEY_PATTERN = /^[A-Za-z0-9_-]{43}$/;

function newKey(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Without browser storage the key lasts until the page closes. */
export function plannerKey(storage: BrowserStorage | undefined): string {
  try {
    const saved = storage?.getItem(PLANNER_KEY_STORAGE_KEY);
    if (saved && KEY_PATTERN.test(saved)) return saved;
    const key = newKey();
    storage?.setItem(PLANNER_KEY_STORAGE_KEY, key);
    return key;
  } catch { return newKey(); }
}

export class SyncRequestError extends Error {
  constructor(message: string, readonly status: number) { super(message); this.name = 'SyncRequestError'; }
}

async function call<T>(fetcher: typeof fetch, key: string, method: string, url: string): Promise<T> {
  return observedRequest(method, url, fetcher, { headers: { 'x-planner-key': key } }, async (response) => {
  if (!response.ok) {
    const payload = await response.json().catch(() => ({})) as { error?: { message?: string } };
    throw new SyncRequestError(payload.error?.message ?? `The planner API answered ${response.status}.`, response.status);
  }
  return response.status === 204 ? undefined as T : await response.json() as T;
  });
}

export interface PairedPhone {
  id: string;
  name: string;
  pairedAt: string;
  lastSeenAt: string;
  connected: boolean;
  downloading?: string;
  delivered: Record<string, string>;
}

/** Compare the stored plan's ride version with what each phone has received. */
export function tripOnPhones(phones: PairedPhone[] | undefined, ride: StoredRide | undefined): string | undefined {
  if (!phones?.length || !ride) return undefined;
  return phones.map((phone) => {
    if (phone.downloading === ride.rideTripId) return `${phone.name} is downloading this trip…`;
    if (phone.delivered[ride.rideTripId] === ride.updatedAt) return `On ${phone.name}`;
    if (phone.connected) return `Sending to ${phone.name}…`;
    return `${phone.name} gets this trip when its app opens`;
  }).join(' · ');
}

/** Reconnect until aborted; every successful connection reports `open` before events. */
export async function followPlannerEvents(key: string, onEvent: (event: string) => void, signal: AbortSignal,
  fetcher: typeof fetch = (...args) => fetch(...args)): Promise<void> {
  while (!signal.aborted) {
    try {
      const response = await fetcher('/api/planner/events', { headers: { 'x-planner-key': key }, signal });
      if (!response.ok || !response.body) throw new Error(`events answered ${response.status}`);
      onEvent('open');
      const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
      let buffer = '';
      try {
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += value.replace(/\r\n/g, '\n');
          let end: number;
          while ((end = buffer.indexOf('\n\n')) >= 0) {
            const block = buffer.slice(0, end);
            buffer = buffer.slice(end + 2);
            const name = block.split('\n').find((line) => line.startsWith('event:'))?.slice(6).trim();
            if (name) onEvent(name);
          }
        }
      } finally { await reader.cancel().catch(() => {}); reader.releaseLock(); }
    } catch { /* The API may be restarting. */ }
    if (signal.aborted) return;
    onEvent('closed');
    await new Promise<void>((resolve) => {
      const finish = () => { clearTimeout(timer); signal.removeEventListener('abort', finish); resolve(); };
      const timer = setTimeout(finish, 3000);
      signal.addEventListener('abort', finish, { once: true });
    });
  }
}

export const phoneApi = (key: string, fetcher: typeof fetch = (...args) => fetch(...args)) => ({
  pairing: () => call<{ url: string; expiresAt: string }>(fetcher, key, 'POST', '/api/planner/pairings'),
  pairingState: async () => (await call<PairingStateReply>(fetcher, key, 'GET', '/api/planner/pairing')).state,
  devices: async () => (await call<{ devices: PairedPhone[] }>(fetcher, key, 'GET', '/api/planner/devices')).devices,
  unpair: async (id: string) => { await call<void>(fetcher, key, 'DELETE', `/api/planner/devices/${encodeURIComponent(id)}`); }
});
